import { useTranslation } from 'react-i18next';
import { useGet } from '@/query/fetchHooks';
import { useState, useContext, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, IconTile, Spinner, ProgressBar } from '@teo/components';
import {
  ArrowLeft,
  Blocks,
  TaskCheckmarkFilledNew,
  StartFlag,
  PersonInfo,
} from '@teo/components/icons';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { nthLastIndexOf, renderTemplate, nthIndexOf } from '@/util';
import { NavContext } from '@/components/NavContext';
import { LearnContent } from '@/components/LearningPath/Learn/LearnContent';
import { sortDataLP } from '@/components/LearningPath/util';
import { axios } from '@/axios';
import { config } from '@/config';
import { MilestonesContent } from '@/components/LearningPath/Learn/MilestonesContent';

function MilestonesPreview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);
  const authUser: any = auth?.user;
  const { trainingId, learningpathId } = useParams();
  const [training, setTraining] = useState<any>(null);
  const { setHeaderDisabled, setLeftSidebar } = useContext(NavContext);

  const [allNodes, setAllNodes] = useState<any>(null);
  const [allMilestones, setAllMilestones] = useState<any>(null);
  const [beforeMilestones, setBeforeMilestones] = useState<any>(null);
  const [nextMilestone, setNextMilestone] = useState<any>(null);

  useGet(`/training/${trainingId}`, {
    onSuccess: async (res: any) => {
      setTraining(res?.data || {});
    },
  });

  useEffect(() => {
    setLeftSidebar(true);
    return () => setLeftSidebar(false);
  });

  useEffect(() => {
    setHeaderDisabled(true);
    return () => setHeaderDisabled(false);
  });

  function getNextById(array: any, id: any) {
    const index = array.findIndex((el: any) => el.objectId === id);
    if (index !== -1 && index < array.length - 1) {
      return array[index + 1];
    }
    return null;
  }

  function getAllBeforeById(array: any, id: any) {
    const index = array.findIndex(
      (el: any) => el.objectId === id || el.id === id
    );
    if (index !== -1) {
      return array.slice(0, index);
    }
    return [];
  }

  function getAllParentsByObjectId(items: any, targetObjectId: any) {
    const byId = new Map(items.map((item: any) => [item.id, item]));
    const start = items.find((item: any) => item.objectId === targetObjectId);

    if (!start) return [];

    const result: any = [];
    const visited = new Set();

    function walk(node: any) {
      if (!node?.roots?.length) return;

      for (const parentId of node.roots) {
        if (visited.has(parentId)) continue;
        visited.add(parentId);

        const parent = byId.get(parentId);
        if (parent) {
          result.push(parent);
          walk(parent);
        }
      }
    }

    walk(start);
    return result;
  }

  function markSelected(main: any, selected: any = [], extra: any = null) {
    const selectedIds = new Set([
      ...selected.map((el: any) => el?.id),
      ...(extra ? [extra?.id] : []),
    ]);

    return main.map((el: any) => ({
      ...el,
      isCompleted: selectedIds.has(el?.id),
    }));
  }

  useGet(`/learningpathnodedetailsflat`, {
    enabled: !!learningpathId,
    topLearningpathId: learningpathId,
    onSuccess: async (res: any) => {
      const lpAllNodes = res?.data?.filter((nds: any) => nds?.type !== 'root');
      const lpContent = lpAllNodes?.filter(
        (nds: any) => nds?.learningpathId === training?.learningpathId
      );
      if (lpContent?.length > 0) {
        const sortData = sortDataLP(lpContent);
        const allLpMilestones = sortData?.filter(
          (nds: any) => nds?.subType === 'milestone'
        );
        const newNodedetails: any[] = [];
        for (const mls of allLpMilestones) {
          let milestonesDetail = null;
          try {
            milestonesDetail = await axios.get(
              `${config.backend}/training/${mls?.objectId}`
            );
            newNodedetails.push(milestonesDetail?.data);
          } catch (error) {
            console.error(error);
          }
        }
        setAllMilestones(
          markSelected(
            newNodedetails,
            getAllBeforeById(newNodedetails, training?.id),
            training
          )
        );
        setBeforeMilestones(getAllBeforeById(newNodedetails, training?.id));
        setNextMilestone(getNextById(allLpMilestones, training?.id));
        const allBefore = getAllParentsByObjectId(sortData, training?.id);
        const lpAllBefore: any = [];
        allBefore?.map((el: any) => {
          lpAllBefore?.push(el);
          if (el?.type === 'learningpath') {
            const thameContent = lpAllNodes?.filter(
              (nds: any) => el?.objectId === nds?.learningpathId
            );
            lpAllBefore?.push(...thameContent);
          }
        });
        setAllNodes(lpAllBefore);
      }
    },
  });

  return (
    <div className="relative -mx-4 flex flex-col">
      <MilestonesContent
        authUser={authUser}
        training={training}
        trainingId={trainingId}
        allMilestones={allMilestones}
        beforeMilestones={beforeMilestones}
        nextMilestone={nextMilestone}
        allNodes={allNodes}
        preview={true}
        allBeforeDone={false}
      />
    </div>
  );
}

export default MilestonesPreview;
